/**
 * Time filter for the campaign performance grid.
 *
 * The grid's question is "which campaigns were RUNNING in this window", so the
 * filter is a flight OVERLAP test, not a point-in-time comparison: a campaign
 * belongs in the window when its flight intersects it at all, even if it
 * started before or ends after.
 *
 * The dates arrive on each row as `flight_start_date` / `flight_end_date` —
 * TEXT, deliberately uncast by the API (routes/v2/campaignGrid.js), read out of
 * the cached metrics JSONB — so everything here treats them as untrusted
 * strings: anything that isn't an ISO date is "unknown", never a throw.
 *
 * Preset vocabulary is shared with the optimizer's window (utils/
 * optimizeDateRange.js) so the two surfaces don't drift apart, plus the
 * calendar presets a grid reader actually asks for (this month / last month).
 */

/** Google Ads reports an open-ended campaign as this sentinel, not as null. */
export const OPEN_ENDED_END_DATE = '2037-12-30';

/**
 * The window choices, in menu order. `days` = a trailing window from today.
 *
 * Five: all time, the three trailing windows, and the custom range. The
 * calendar presets (this/last month, YTD) stay out of the menu — every preset
 * change BLANKS the grid and auto-pulls the reporting APIs for the new
 * window, so each menu entry is a real pull the user pays for, not a free
 * client-side filter. The custom range is the one deliberate exception: it
 * defers to explicit dates (resolveTimeWindow), and the reporting-API pull
 * waits for the toolbar's Fetch button instead of firing the moment both
 * dates happen to be filled in. Legacy stored values degrade to the default
 * via loadStoredTimeFilter's vocabulary check; presetToWindow still resolves
 * the legacy spellings so an in-flight stored window keeps describing
 * itself.
 */
export const GRID_TIME_PRESETS = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days', days: 7 },
  { value: 'last_30_days', label: 'Last 30 days', days: 30 },
  { value: 'last_90_days', label: 'Last 90 days', days: 90 },
  { value: 'custom', label: 'Custom range…' },
];

/**
 * The grid's default window: ALL TIME. The previous last-30 default existed
 * to stay consistent with the 4h cron pull; that cron no longer schedules the
 * grid pull (user-driven pulls own the dataset now — every window change
 * triggers its own refresh), so the default reverts to the whole story and
 * the picker's persisted choice carries between visits.
 */
export const DEFAULT_GRID_TIME_PRESET = 'all_time';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

const iso = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/**
 * An ISO `YYYY-MM-DD` prefix, or null for anything unusable (empty, null,
 * '—', a timestamp we can't parse, junk from a vendor export).
 *
 * Compared as STRINGS by the callers below: ISO dates sort lexicographically,
 * which sidesteps every timezone question a Date round-trip would introduce.
 */
export function isoDay(value) {
  const m = ISO_DATE_RE.exec(String(value ?? '').trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * A preset → `{ startDate, endDate }` (inclusive, ISO), or null when the
 * preset spans everything (`all_time`) or defers to explicit dates
 * (`custom`).
 *
 * `today` is injectable so tests don't depend on the clock.
 */
export function presetToWindow(preset, today = new Date()) {
  const t = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  if (!preset || preset === 'all_time' || preset === 'custom') return null;

  // Trailing windows (the menu) + legacy spellings (still resolvable so a
  // stored window from the old menu keeps describing itself).
  const days = GRID_TIME_PRESETS.find((p) => p.value === preset)?.days
    || { last_7_days: 7, last_30_days: 30, last_90_days: 90 }[preset];
  if (days) {
    const start = new Date(t);
    // Inclusive of today: "last 7 days" is today plus the 6 before it, which
    // is what a reader counting rows expects.
    start.setUTCDate(start.getUTCDate() - (days - 1));
    return { startDate: iso(start), endDate: iso(t) };
  }
  if (preset === 'this_month') {
    return {
      startDate: iso(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1))),
      endDate: iso(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0))),
    };
  }
  if (preset === 'last_month') {
    return {
      startDate: iso(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() - 1, 1))),
      endDate: iso(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 0))),
    };
  }
  if (preset === 'ytd') {
    return {
      startDate: iso(new Date(Date.UTC(t.getUTCFullYear(), 0, 1))),
      endDate: iso(t),
    };
  }
  return null;
}

/**
 * The effective window for a filter state, or null for "no filtering".
 *
 * @param {{preset: string, startDate?: string, endDate?: string}} state
 */
export function resolveTimeWindow(state, today = new Date()) {
  if (!state?.preset || state.preset === 'all_time') return null;
  if (state.preset === 'custom') {
    const startDate = isoDay(state.startDate);
    const endDate = isoDay(state.endDate);
    // A half-filled custom range is a window the user is still typing — treat
    // it as open on the missing side rather than showing an empty grid.
    if (!startDate && !endDate) return null;
    if (startDate && endDate && endDate < startDate) return { startDate: endDate, endDate: startDate };
    return { startDate, endDate };
  }
  return presetToWindow(state.preset, today);
}

/**
 * Does this campaign's flight overlap the window?
 *
 * Rules, each one a real case in the data:
 *   • no window            → everything matches;
 *   • no usable dates      → MATCHES. A campaign whose platform pull carried
 *     no flight (or hasn't been pulled yet) must not vanish from the grid
 *     because a filter was applied — hiding rows for missing metadata is how
 *     a filter turns into apparent data loss;
 *   • open-ended end       → treated as "still running" (null, or Google's
 *     2037-12-30 sentinel);
 *   • otherwise            → start <= windowEnd AND end >= windowStart.
 */
export function campaignInWindow(campaign, window) {
  if (!window) return true;
  const start = isoDay(campaign?.flight_start_date);
  const rawEnd = isoDay(campaign?.flight_end_date);
  const openEnded = !rawEnd || rawEnd.startsWith(OPEN_ENDED_END_DATE);
  const end = openEnded ? null : rawEnd;
  if (!start && !end) return true;
  if (window.endDate && start && start > window.endDate) return false;
  if (window.startDate && end && end < window.startDate) return false;
  return true;
}

/** Human label for the active window — for the toolbar's count line. */
export function describeTimeWindow(window) {
  if (!window) return 'All time';
  if (window.startDate && window.endDate) return `${window.startDate} → ${window.endDate}`;
  if (window.startDate) return `From ${window.startDate}`;
  return `Through ${window.endDate}`;
}

/**
 * The window of the LATEST successful pull — read from the newest pulled
 * row's metrics_window stamp (what finalizeRun wrote), never from a picker.
 * Shown on the Triggers page, which has no picker of its own: it reads the
 * campaign-grid dataset as persisted, and this says what that dataset's most
 * recent pull covered.
 *
 * Older rows may still carry other windows from earlier pulls; the label
 * deliberately reports only the newest pull. (A prior version audited
 * uniformity and printed "Data: mixed windows — newest pull …", which read
 * as noise — the reader wants the range, not the audit.)
 *
 * Returns null when nothing has been pulled at all.
 *
 * @param {Array} campaigns grid rows ({ metrics_window, metrics_fetched_at })
 * @returns {{ kind: 'lifetime'|'windowed',
 *             window: {startDate, endDate}|null, label: string }|null}
 */
export function latestPullWindow(campaigns) {
  const pulled = (campaigns || []).filter((c) => c && c.metrics_fetched_at);
  if (pulled.length === 0) return null;
  const newest = pulled.reduce((a, b) => (
    new Date(a.metrics_fetched_at).getTime() >= new Date(b.metrics_fetched_at).getTime() ? a : b
  ));
  const mw = newest.metrics_window;
  // No stamp, or a stamp with no dates: the pull ran unwindowed (all time).
  if (!mw || typeof mw !== 'object' || (!mw.start_date && !mw.end_date)) {
    return { kind: 'lifetime', window: null, label: 'Data: all time' };
  }
  const window = { startDate: mw.start_date || '', endDate: mw.end_date || '' };
  return { kind: 'windowed', window, label: `Data: ${describeTimeWindow(window)}` };
}

// ── Remembered filter ────────────────────────────────────────────────────────
// The picker used to reset to "All time" on every page load, silently parting
// ways with whatever window the cached dataset still carried. Persisted per
// account: two accounts' grids are different datasets with different filters.

// `scope` namespaces the key so two surfaces sharing this vocabulary do not
// share one remembered window. They must not: a preset change on the grid
// dispatches a paid reporting-API pull, while the Account read re-derives its
// numbers from the last pull's artifact for free — so a window that is ordinary
// to pick on one page would silently spend money if it carried to the other.
const timeFilterStorageKey = (accountId, scope = 'campaignGrid') => `${scope}:timeFilter:${accountId || 'default'}`;

/** The stored filter, or the default. Anything unreadable degrades to default. */
export function loadStoredTimeFilter(accountId, scope) {
  const fallback = { preset: DEFAULT_GRID_TIME_PRESET, startDate: '', endDate: '' };
  try {
    const raw = localStorage.getItem(timeFilterStorageKey(accountId, scope));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!GRID_TIME_PRESETS.some((preset) => preset.value === parsed?.preset)) return fallback;
    return {
      preset: parsed.preset,
      startDate: typeof parsed.startDate === 'string' ? parsed.startDate : '',
      endDate: typeof parsed.endDate === 'string' ? parsed.endDate : '',
    };
  } catch (e) {
    return fallback;
  }
}

export function storeTimeFilter(accountId, filter, scope) {
  try {
    localStorage.setItem(timeFilterStorageKey(accountId, scope), JSON.stringify({
      preset: filter.preset, startDate: filter.startDate || '', endDate: filter.endDate || '',
    }));
  } catch (e) { /* storage full/blocked — the filter just won't persist */ }
}

export default {
  GRID_TIME_PRESETS,
  DEFAULT_GRID_TIME_PRESET,
  OPEN_ENDED_END_DATE,
  isoDay,
  presetToWindow,
  resolveTimeWindow,
  campaignInWindow,
  describeTimeWindow,
  latestPullWindow,
  loadStoredTimeFilter,
  storeTimeFilter,
};
